/**
 * Last-task memory (iter 83).
 *
 * Persists which of the 4 generation tasks (`enhance` / `edit` /
 * `match-style` / `merge`) the user last picked, workspace-scoped. So a
 * user who always works in Match Style doesn't have to re-pick on every
 * studio open.
 *
 * Stored in `localStorage` under `studio.lastTask.<workspaceId>`.
 *
 * Defensive: if the stored value isn't one of the 4 known tasks (the
 * `GenerateTask` enum changed, the storage was tampered with, JSON
 * parse failed), returns `null` so the caller falls back to its default.
 */

import { GENERATE_TASKS, type GenerateTask } from '@studio/contract';

const KEY_PREFIX = 'studio.lastTask.';

const VALID_TASKS = new Set<string>(GENERATE_TASKS);

export function readLastTask(workspaceId: string | null): GenerateTask | null {
  if (typeof window === 'undefined' || !workspaceId) return null;
  try {
    const raw = window.localStorage.getItem(KEY_PREFIX + workspaceId);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'string' || !VALID_TASKS.has(parsed)) return null;
    return parsed as GenerateTask;
  } catch {
    return null;
  }
}

export function writeLastTask(workspaceId: string | null, task: GenerateTask): void {
  if (typeof window === 'undefined' || !workspaceId) return;
  try {
    window.localStorage.setItem(KEY_PREFIX + workspaceId, JSON.stringify(task));
  } catch {
    // ignore quota / private-mode errors
  }
}
